// Two small rules the enrolment screens share, kept out of the components so they can be tested
// without dragging the whole React tree in.

/**
 * Should the office config be fetched right now?
 *
 * AddContact only loaded it when EDITING an existing lead (`!isCreate && getData()`), so on the
 * create path contactGroupConfig stayed null, the flat-fee control could never render, and a rep
 * could not choose a flat fee on a client they had just created. Yulia, 8 Sep: "при першому
 * створенні ліда немає flat fee".
 *
 * There is nothing to ask about until the contact exists — the office is resolved from the contact —
 * so this waits for the id BasicInfo creates, and asks once.
 *
 * The condition is whether the FEE SHAPE is known, not whether some config object exists. Another
 * path (loadCreatorGroupConfig) sets a config carrying plan-defaults and disable-initial-fee but no
 * feeShape; treating that as "already loaded" suppressed the fetch and hid the picker again for
 * exactly the offices that configure those things.
 */
export const needsOfficeConfig = ({ isCreate, custUserId, contactGroupConfig }) =>
  !!isCreate && !!custUserId && !contactGroupConfig?.feeShape

/**
 * Is the program plan too incomplete to save?
 *
 * The percentage requirement is judged on the field each shape actually uses. Gating on programFee
 * for everyone meant a flat client passed with no amount at all — programFee is hidden for them and
 * defaults to a value — so picking Flat and saving silently stored a percentage.
 */
export const isProgramPlanIncomplete = ({ programPlan, userRole, hasStartDate }) => {
  const isFlat = programPlan?.feeType === 'flat'

  const feeMissing = isFlat
    ? !(Number(programPlan?.flatFee) > 0)
    : userRole !== 'customer' && !(Number(programPlan?.programFee) > 0)

  return feeMissing || !programPlan?.plan || !hasStartDate
}

/**
 * The whole program-plan change when the rep picks a fee shape.
 *
 * Picking Flat starts the plan at ONE payment. A flat fee is often collected in a single charge —
 * Dolly on Solar: "$7,500.00 either in one installment or across 36 months" — and the length a
 * percentage client happened to be on is not a sensible starting point for that. It is a default,
 * not a rule; the rep changes it freely afterwards.
 *
 * Switching back restores the length they came from, because leaving a percentage client on a
 * single payment is just as wrong in the other direction. Re-picking the shape you are already on
 * changes nothing, so a length the rep has just set is never overwritten.
 *
 * A percentage client carries no flat amount, so the two can never disagree about what was agreed.
 */
export const applyFeeShapePick = ({ prev = {}, nextType }) => {
  const wasFlat = prev?.feeType === 'flat'
  const toFlat = nextType === 'flat'

  if (wasFlat === toFlat) {
    return { ...prev, feeType: nextType }
  }

  return {
    ...prev,
    feeType: nextType,
    flatFee: toFlat ? (prev?.flatFee ?? null) : null,
    plan: toFlat ? 1 : (prev?.planBeforeFlat ?? prev?.plan),
    planBeforeFlat: toFlat ? prev?.plan : undefined,
  }
}
